/**
 * Scope tier for an MCP OAuth connection. Mirrors `McpConnectionEntity.scope`
 * in `@novu/dal`. Only `subscriber` is implemented in v1; the remaining tiers
 * are reserved for future shared-token flows.
 */
export var McpConnectionScopeEnum;
(function (McpConnectionScopeEnum) {
    McpConnectionScopeEnum["Environment"] = "environment";
    McpConnectionScopeEnum["Agent"] = "agent";
    McpConnectionScopeEnum["Subscriber"] = "subscriber";
})(McpConnectionScopeEnum || (McpConnectionScopeEnum = {}));
/**
 * OAuth mechanism the connection was established with. Mirrors the catalog
 * `mode` for the MCP — each MCP supports exactly one mechanism.
 *
 * - `dcr`              — Dynamic Client Registration (RFC 7591). A fresh OAuth
 *                        client is registered per subscriber against the
 *                        upstream AS.
 * - `novu-app`         — Novu's single pre-registered OAuth application is
 *                        used. `client_id` / `client_secret` come from server
 *                        env vars.
 * - `user-app`         — The Novu customer's own pre-registered OAuth
 *                        application is used. Credentials come from a per-org
 *                        credential table.
 * - `provider-managed` — OAuth is fully delegated to the managed agent runtime
 *                        provider (e.g. Claude). Novu never runs the OAuth
 *                        dance, never exchanges codes, and never stores
 *                        access/refresh tokens for these MCPs — it only
 *                        ensures the provider vault exists and redirects the
 *                        user into the provider's vault UI so they can
 *                        complete connector auth there.
 */
export var McpConnectionAuthModeEnum;
(function (McpConnectionAuthModeEnum) {
    McpConnectionAuthModeEnum["Dcr"] = "dcr";
    McpConnectionAuthModeEnum["NovuApp"] = "novu-app";
    McpConnectionAuthModeEnum["UserApp"] = "user-app";
    McpConnectionAuthModeEnum["ProviderManaged"] = "provider-managed";
})(McpConnectionAuthModeEnum || (McpConnectionAuthModeEnum = {}));
/**
 * Concrete list backing `McpTokenEndpointAuthMethod`. Kept in lock-step with
 * the union via `satisfies` so the Mongoose `enum` array, registration code,
 * and exhaustive switches all derive from one place.
 */
export const MCP_TOKEN_ENDPOINT_AUTH_METHODS = [
    'client_secret_basic',
    'client_secret_post',
    'none',
];
/**
 * Default `token_endpoint_auth_method` used when a persisted MCP OAuth client
 * row pre-dates negotiation (no value stored) or when an AS metadata document
 * omits `token_endpoint_auth_methods_supported`. RFC 8414 §2 defines this
 * default as `client_secret_basic`.
 */
export const DEFAULT_MCP_TOKEN_ENDPOINT_AUTH_METHOD = 'client_secret_basic';
/**
 * Resolve a persisted `token_endpoint_auth_method` against the RFC 8414 §2
 * default. Use everywhere a legacy row (no value stored) needs to be coerced
 * into a usable method — keeps the fallback literal in exactly one place.
 */
export function resolvePersistedMcpTokenEndpointAuthMethod(value) {
    return value ?? DEFAULT_MCP_TOKEN_ENDPOINT_AUTH_METHOD;
}
export var McpConnectionStatusEnum;
(function (McpConnectionStatusEnum) {
    McpConnectionStatusEnum["PendingOAuth"] = "pending_oauth";
    McpConnectionStatusEnum["Connected"] = "connected";
    McpConnectionStatusEnum["Expired"] = "expired";
    McpConnectionStatusEnum["Revoked"] = "revoked";
    McpConnectionStatusEnum["Error"] = "error";
})(McpConnectionStatusEnum || (McpConnectionStatusEnum = {}));
//# sourceMappingURL=managed-runtime.dto.js.map